import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { request, setTokenGetter } from './api';
import { apiSignIn, apiSignOut, apiSignUp, getAccessToken, readSession, SESSION_KEY, storeSession } from './api-session';
import { config } from './config';

export interface AuthUser { id: string; email: string | null }

interface AuthApi {
  user: AuthUser | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, fullName: string, locale: string): Promise<{ needsConfirmation: boolean }>;
  signOut(): Promise<void>;
  /** `unavailable`: no email service is configured; ask a platform admin for a reset link. */
  resetPassword(email: string): Promise<{ delivery: 'email' | 'log' | 'unavailable' }>;
  /** Sets a new password: via the emailed `token` (api mode) or the recovery session (Supabase). */
  updatePassword(password: string, token?: string): Promise<void>;
  /** Signed-in password change. `current` is required with built-in sign-in. */
  changePassword(current: string, next: string): Promise<void>;
  mode: 'supabase' | 'api' | 'dev';
}

const AuthContext = createContext<AuthApi | null>(null);

let supabase: SupabaseClient | null = null;
function getSupabase() {
  if (!supabase) {
    if (!config.supabaseUrl || !config.supabaseAnonKey) throw new Error('Supabase is not configured (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)');
    supabase = createClient(config.supabaseUrl, config.supabaseAnonKey, { auth: { persistSession: true, autoRefreshToken: true } });
  }
  return supabase;
}

const DEV_KEY = 'highcrm.dev-session';

/**
 * Sign-in backends (VITE_AUTH_MODE): `supabase` (Supabase Auth), `api` (the
 * API's built-in sign-in, e.g. on Railway) or `dev` (local development).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (config.authMode === 'api') {
      const signedOut = () => setUser(null);
      setTokenGetter(() => getAccessToken(signedOut));
      const sync = () => {
        const session = readSession();
        setUser(session ? { id: session.user.id, email: session.user.email } : null);
      };
      sync();
      setLoading(false);
      // Sign-in/out in another tab.
      const onStorage = (e: StorageEvent) => { if (e.key === SESSION_KEY || e.key === null) sync(); };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    if (config.authMode === 'dev') {
      const stored = localStorage.getItem(DEV_KEY);
      const session = stored ? (JSON.parse(stored) as { token: string; user: AuthUser }) : null;
      setTokenGetter(async () => (JSON.parse(localStorage.getItem(DEV_KEY) ?? 'null') as { token: string } | null)?.token ?? null);
      setUser(session?.user ?? null);
      setLoading(false);
      return;
    }
    const client = getSupabase();
    setTokenGetter(async () => (await client.auth.getSession()).data.session?.access_token ?? null);
    client.auth.getSession().then(({ data }) => {
      setUser(data.session ? { id: data.session.user.id, email: data.session.user.email ?? null } : null);
      setLoading(false);
    });
    const { data } = client.auth.onAuthStateChange((_event, session) => {
      setUser(session ? { id: session.user.id, email: session.user.email ?? null } : null);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthApi>(() => ({
    user,
    loading,
    mode: config.authMode,
    async signIn(email, password) {
      if (config.authMode === 'api') {
        const session = await apiSignIn(email, password);
        setUser(session ? { id: session.user.id, email: session.user.email } : null);
        return;
      }
      if (config.authMode === 'dev') {
        const res = await request<{ access_token: string; user: { id: string; email: string } }>('/api/dev/login',
          { method: 'POST', body: { email }, org: false });
        localStorage.setItem(DEV_KEY, JSON.stringify({ token: res.access_token, user: res.user }));
        setUser(res.user);
        return;
      }
      const { error } = await getSupabase().auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    async signUp(email, password, fullName, locale) {
      if (config.authMode === 'api') {
        const session = await apiSignUp(email, password, fullName, locale);
        setUser(session ? { id: session.user.id, email: session.user.email } : null);
        return { needsConfirmation: false };
      }
      if (config.authMode === 'dev') {
        const res = await request<{ access_token: string; user: { id: string; email: string } }>('/api/dev/login',
          { method: 'POST', body: { email, full_name: fullName }, org: false });
        localStorage.setItem(DEV_KEY, JSON.stringify({ token: res.access_token, user: res.user }));
        setUser(res.user);
        return { needsConfirmation: false };
      }
      const { data, error } = await getSupabase().auth.signUp({
        email, password,
        options: { data: { full_name: fullName, locale }, emailRedirectTo: `${window.location.origin}/onboarding` },
      });
      if (error) throw error;
      return { needsConfirmation: !data.session };
    },
    async signOut() {
      if (config.authMode === 'api') await apiSignOut();
      else if (config.authMode === 'dev') localStorage.removeItem(DEV_KEY);
      else await getSupabase().auth.signOut();
      setUser(null);
    },
    async resetPassword(email) {
      if (config.authMode === 'api') {
        return request<{ delivery: 'email' | 'log' | 'unavailable' }>('/api/auth/password/forgot',
          { method: 'POST', body: { email }, org: false, auth: false });
      }
      if (config.authMode === 'dev') return { delivery: 'log' };
      const { error } = await getSupabase().auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
      if (error) throw error;
      return { delivery: 'email' };
    },
    async updatePassword(password, token) {
      if (config.authMode === 'api') {
        await request('/api/auth/password/reset', { method: 'POST', body: { token, password }, org: false, auth: false });
        return;
      }
      if (config.authMode === 'dev') return;
      const { error } = await getSupabase().auth.updateUser({ password });
      if (error) throw error;
    },
    async changePassword(current, next) {
      if (config.authMode === 'api') {
        const res = await request<{ access_token: string; refresh_token: string; expires_in: number; user: { id: string; email: string; full_name: string | null } }>(
          '/api/auth/password/change', { method: 'POST', body: { current_password: current, new_password: next }, org: false });
        storeSession(res);
        return;
      }
      if (config.authMode === 'dev') return;
      const { error } = await getSupabase().auth.updateUser({ password: next });
      if (error) throw error;
    },
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
